import { EquityBridgeColumnRole } from '@app/shared/models/contracts/enums/shared-enums';
import { DebtItemDto, EquityBridgeDto } from '@app/shared/models/contracts/project-dto';
import { v4 as uuidv4 } from 'uuid';

export const mergeOpmNetDebtIntoEquityBridge = (
  currentEquityBridge: EquityBridgeDto | undefined,
  opmNetDebtItems: DebtItemDto[],
  submittedItemIds: string[] = [],
  deletedItemIds: string[] = [],
  valuationCashValue?: Nullable<number>
): EquityBridgeDto => {
  const equityBridge = currentEquityBridge ?? {
    columns: [],
    netDebtItems: [],
    commonNetDebtItemIds: [],
  };
  const deletedItemIdSet = new Set(deletedItemIds);
  const netDebtItems = equityBridge.netDebtItems.filter(
    (item) => !deletedItemIdSet.has(item.netDebtItemId)
  );
  const commonNetDebtItemIds = new Set(
    equityBridge.commonNetDebtItemIds.filter((itemId) => !deletedItemIdSet.has(itemId))
  );
  const submittedItemIdSet = new Set(submittedItemIds);
  const promotedItems = opmNetDebtItems.filter(
    (item) => submittedItemIdSet.has(item.netDebtItemId) && item.historicalValue != null
  );
  const valuesToSync = opmNetDebtItems.filter(
    (item) =>
      submittedItemIdSet.has(item.netDebtItemId) &&
      (item.historicalValue != null || commonNetDebtItemIds.has(item.netDebtItemId))
  );

  opmNetDebtItems.forEach((item) => {
    const existingIndex = netDebtItems.findIndex(
      (bridgeItem) => bridgeItem.netDebtItemId === item.netDebtItemId
    );
    if (existingIndex === -1) {
      netDebtItems.push({ netDebtItemId: item.netDebtItemId, narrative: item.narrative });
    } else if (
      submittedItemIdSet.has(item.netDebtItemId) ||
      !netDebtItems[existingIndex].narrative
    ) {
      netDebtItems[existingIndex] = {
        ...netDebtItems[existingIndex],
        narrative: item.narrative || netDebtItems[existingIndex].narrative,
      };
    }
  });

  promotedItems.forEach((item) => commonNetDebtItemIds.add(item.netDebtItemId));
  const valuesToSyncById = Object.fromEntries(
    valuesToSync.map((item) => [item.netDebtItemId, item.historicalValue])
  );
  let columns =
    deletedItemIds.length > 0
      ? equityBridge.columns.map((column) => ({
          ...column,
          netDebtValues: Object.fromEntries(
            Object.entries(column.netDebtValues).filter(([itemId]) => !deletedItemIdSet.has(itemId))
          ),
        }))
      : equityBridge.columns;

  const shouldSyncValuationColumn = valuesToSync.length > 0 || valuationCashValue !== undefined;
  if (shouldSyncValuationColumn) {
    const valuationColumnIndex = columns.findIndex(
      (column) => column.role === EquityBridgeColumnRole.Valuation
    );
    if (
      valuationColumnIndex === -1 &&
      (promotedItems.length > 0 || valuationCashValue !== undefined)
    ) {
      columns = [
        ...columns,
        {
          columnId: uuidv4(),
          dateMemo: '',
          role: EquityBridgeColumnRole.Valuation,
          equityValue: null,
          cashValue: valuationCashValue ?? null,
          enterpriseValue: null,
          netDebtValues: valuesToSyncById,
        },
      ];
    } else if (valuationColumnIndex !== -1) {
      columns = columns.map((column, index) =>
        index === valuationColumnIndex
          ? {
              ...column,
              cashValue: valuationCashValue !== undefined ? valuationCashValue : column.cashValue,
              netDebtValues: { ...column.netDebtValues, ...valuesToSyncById },
            }
          : column
      );
    }
  }

  return {
    ...equityBridge,
    columns,
    netDebtItems,
    commonNetDebtItemIds: Array.from(commonNetDebtItemIds),
  };
};
